import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets scroll to the top of the page on every route change.
 *
 * React Router v6 does NOT auto-scroll to top when navigating between routes
 * (it preserves the scroll position from the previous page). This breaks
 * the mobile experience when users tap footer links such as Terms, Privacy,
 * Refund Policy, Cookies, Organiser/Payout Terms — the new page mounts but
 * the viewport stays scrolled down where the footer was, so the page title
 * is invisible until the user manually scrolls up.
 *
 * If the URL includes a #hash, we honour that anchor instead — using
 * scrollIntoView with `behavior: smooth` and a small offset to clear the
 * fixed header.
 *
 * Mount once inside <BrowserRouter>, above the <Routes>.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Defer until the destination DOM is mounted.
      const id = hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return;
    }
    // Plain route change → instant top so the user sees the page header.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
